import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PlayArrow as PlayArrowIcon,
  ToggleOff as ToggleOffIcon,
} from '@mui/icons-material';
import http from '../../services/http';
import { toast } from 'react-toastify';
import { ThemeContext } from '../ThemeContext/ThemeContext';

const parseDocTags = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
  }
  if (raw == null || raw === '') return [];
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p)) {
        return p.map((t) => String(t).trim().toLowerCase()).filter(Boolean);
      }
    } catch {
      return raw
        .split(',')
        .map((t) => t.trim().toLowerCase())
        .filter(Boolean);
    }
  }
  return [];
};

const mergePendingTagInput = (tags, pendingInput) => {
  const base = parseDocTags(tags);
  const p = String(pendingInput || '').trim().toLowerCase();
  if (p && !base.includes(p)) base.push(p);
  return [...new Set(base)];
};

const AIConfigurationPage = () => {
  const { theme } = useContext(ThemeContext);
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(false);

  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [configForm, setConfigForm] = useState({
    name: '',
    provider: 'openai',
    api_key: '',
    model: '',
    system_instructions: '',
    text_tools_instructions: '',
    active: false,
    documents_use_all_tags: true,
    document_tags: [],
  });
  const [testingConfig, setTestingConfig] = useState(null);
  const [availableModels, setAvailableModels] = useState([]);
  const [loadingModels, setLoadingModels] = useState(false);

  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [configDocTagInput, setConfigDocTagInput] = useState('');

  const tagOptionsForConfig = useMemo(() => {
    const set = new Set(tagSuggestions.map((t) => String(t).toLowerCase()));
    (configForm.document_tags || []).forEach((t) => set.add(String(t).toLowerCase()));
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tagSuggestions, configForm.document_tags]);

  const activeConfigurations = useMemo(
    () => (configurations || []).filter((c) => c.active),
    [configurations]
  );

  const TEXT_TOOLS_CONFIG_STORAGE_KEY = 'ai_text_tools_configuration_id';
  const [textToolsConfigId, setTextToolsConfigId] = useState(() => {
    try {
      const raw = localStorage.getItem(TEXT_TOOLS_CONFIG_STORAGE_KEY);
      if (raw == null || raw === '') return '';
      const n = Number(raw);
      return !Number.isNaN(n) && n > 0 ? n : '';
    } catch {
      return '';
    }
  });

  const activeCompleteConfigurations = useMemo(
    () => (activeConfigurations || []).filter((c) => c.is_complete),
    [activeConfigurations]
  );

  const loadTagSuggestions = useCallback(async () => {
    try {
      const res = await http.get('/ai-documents/tags/list');
      if (res.data?.success) {
        setTagSuggestions(res.data.tags || []);
      }
    } catch (e) {}
  }, []);

  const loadConfigurations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await http.get('/ai-configurations');
      if (response.data.success) {
        setConfigurations(response.data.configurations || []);
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfigurations();
    loadTagSuggestions();
  }, [loadConfigurations, loadTagSuggestions]);

  const handleOpenConfigModal = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setConfigForm({
        name: config.name,
        provider: config.provider,
        api_key: '',
        model: config.model || '',
        system_instructions: config.system_instructions || '',
        text_tools_instructions: config.text_tools_instructions || '',
        active: config.active,
        documents_use_all_tags: config.documents_use_all_tags !== false,
        document_tags: Array.isArray(config.document_tags) ? [...config.document_tags] : [],
      });
      if (config.available_models && config.available_models.length > 0) {
        setAvailableModels(config.available_models);
      } else {
        setAvailableModels([]);
      }
    } else {
      setEditingConfig(null);
      setConfigForm({
        name: '',
        provider: 'openai',
        api_key: '',
        model: '',
        system_instructions: '',
        text_tools_instructions: '',
        active: false,
        documents_use_all_tags: true,
        document_tags: [],
      });
      setAvailableModels([]);
    }
    setConfigDocTagInput('');
    setConfigModalOpen(true);
  };

  const handleTestApiKey = async () => {
    if (!configForm.api_key || !configForm.provider) {
      toast.warning('Preencha o provedor e a API key antes de buscar modelos');
      return;
    }

    try {
      setLoadingModels(true);

      const tempData = {
        name: 'temp_' + Date.now(),
        provider: configForm.provider,
        api_key: configForm.api_key,
        model: configForm.model || '',
        system_instructions: '',
        active: false,
        documents_use_all_tags: true,
        document_tags: [],
      };

      let tempConfigId = null;
      try {
        const createResponse = await http.post('/ai-configurations', tempData);
        if (createResponse.data.success) {
          tempConfigId = createResponse.data.configuration.id;

          const testResponse = await http.post(`/ai-configurations/${tempConfigId}/test`);

          const configResponse = await http.get(`/ai-configurations/${tempConfigId}`);
          if (configResponse.data.success && configResponse.data.configuration.available_models) {
            const models = configResponse.data.configuration.available_models;
            setAvailableModels(models);
            
            if (models.length > 0 && !configForm.model) {
              const defaultModel = models.find(m => m.includes('gemini-2.5-flash')) || models[0];
              setConfigForm({ ...configForm, model: defaultModel });
            }
            
            if (testResponse.data.success) {
              toast.success(`${models.length} modelo(s) encontrado(s)! ${testResponse.data.message || ''}`);
            } else {
              toast.warning(`${models.length} modelo(s) encontrado(s), mas o teste falhou: ${testResponse.data.error || ''}`);
            }
          } else {
            let defaultModels = [];
            if (configForm.provider === 'gemini') {
              defaultModels = ['gemini-2.5-flash', 'gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-1.0-pro', 'gemini-pro'];
            } else if (configForm.provider === 'openai') {
              defaultModels = ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'];
            } else if (configForm.provider === 'claude') {
              defaultModels = ['claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022', 'claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'];
            }
            setAvailableModels(defaultModels);
            if (defaultModels.length > 0 && !configForm.model) {
              setConfigForm({ ...configForm, model: defaultModels[0] });
            }
            toast.info(`Usando lista padrão de modelos (${defaultModels.length} modelo(s))`);
          }
        }
      } finally {
        if (tempConfigId) {
          try {
            await http.delete(`/ai-configurations/${tempConfigId}`);
          } catch (e) {}
        }
      }
    } catch (error) {
      console.error('Erro ao buscar modelos:', error);
      let defaultModels = [];
      if (configForm.provider === 'gemini') {
        defaultModels = ['gemini-2.5-flash', 'gemini-1.5-flash', 'gemini-1.5-pro', 'gemini-1.0-pro', 'gemini-pro'];
      } else if (configForm.provider === 'openai') {
        defaultModels = ['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo'];
      } else if (configForm.provider === 'claude') {
        defaultModels = ['claude-3-5-sonnet-20241022', 'claude-3-5-haiku-20241022', 'claude-3-opus-20240229', 'claude-3-sonnet-20240229', 'claude-3-haiku-20240307'];
      }
      setAvailableModels(defaultModels);
      if (defaultModels.length > 0 && !configForm.model) {
        setConfigForm({ ...configForm, model: defaultModels[0] });
      }
      toast.error(error.response?.data?.error || 'Erro ao buscar modelos. Usando lista padrão.');
    } finally {
      setLoadingModels(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setLoading(true);
      const document_tags_merged = !configForm.documents_use_all_tags
        ? mergePendingTagInput(configForm.document_tags, configDocTagInput)
        : configForm.document_tags;

      const data = {
        ...configForm,
        document_tags: document_tags_merged,
        api_key: configForm.api_key || undefined,
      };

      if (editingConfig) {
        await http.put(`/ai-configurations/${editingConfig.id}`, data);
        toast.success('Configuração atualizada com sucesso!');
      } else {
        await http.post('/ai-configurations', data);
        toast.success('Configuração criada com sucesso!');
      }

      setConfigDocTagInput('');
      setConfigModalOpen(false);
      loadConfigurations();
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast.error(error.response?.data?.error || 'Erro ao salvar configuração');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateConfig = async (id) => {
    try {
      await http.post(`/ai-configurations/${id}/activate`);
      toast.success('Configuração ativada com sucesso!');
      loadConfigurations();
    } catch (error) {
      console.error('Erro ao ativar configuração:', error);
      toast.error(error.response?.data?.error || 'Erro ao ativar configuração');
    }
  };

  const handleDeactivateConfig = async (id) => {
    try {
      await http.put(`/ai-configurations/${id}`, { active: false });
      toast.success('Configuração desativada.');
      loadConfigurations();
    } catch (error) {
      console.error('Erro ao desativar configuração:', error);
      toast.error(error.response?.data?.error || 'Erro ao desativar configuração');
    }
  };

  const handleTestConfig = async (id) => {
    try {
      setTestingConfig(id);
      const response = await http.post(`/ai-configurations/${id}/test`);
      if (response.data.success) {
        toast.success(response.data.message || 'Teste realizado com sucesso!');
        loadConfigurations();
      } else {
        toast.error(response.data.error || 'Erro no teste');
      }
    } catch (error) {
      console.error('Erro ao testar configuração:', error);
      toast.error(error.response?.data?.error || 'Erro ao testar configuração');
    } finally {
      setTestingConfig(null);
    }
  };

  const handleDeleteConfig = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta configuração?')) {
      return;
    }
    try {
      await http.delete(`/ai-configurations/${id}`);
      toast.success('Configuração deletada com sucesso!');
      loadConfigurations();
    } catch (error) {
      console.error('Erro ao deletar configuração:', error);
      toast.error('Erro ao deletar configuração');
    }
  };

  return (
    <Card
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Configurações de IA
              </Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                Provedores, modelos e regras do Orby e das ferramentas de ajuste de texto.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenConfigModal()}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Nova Configuração
            </Button>
          </Box>

          {activeConfigurations.length > 0 && (
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2" component="div">
                <strong>
                  {activeConfigurations.length === 1
                    ? 'Disponível no Orby'
                    : `Disponíveis no Orby (${activeConfigurations.length})`}
                  :
                </strong>
                <Box component="ul" sx={{ m: 0, mt: 1, mb: 0, pl: 2.5 }}>
                  {activeConfigurations.map((c) => (
                    <li key={c.id}>
                      {c.name} ({c.provider}
                      {c.model ? ` — ${c.model}` : ''})
                    </li>
                  ))}
                </Box>
              </Typography>
            </Alert>
          )}

          <Card variant="outlined" sx={{ mb: 3, borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ p: 3, '&:last-child': { pb: 3 } }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5, color: theme.palette.text.primary }}>
                Preferência para “Ajustar texto”
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Escolha qual configuração (OpenAI/Claude/Gemini) será usada nos botões de ajuste de texto nos tickets.
                Isso não altera o “Orby/Assistente”.
              </Typography>

              <FormControl size="small" sx={{ minWidth: 320, maxWidth: '100%' }}>
                <InputLabel id="text-tools-ai-config-label" shrink>Configuração para ajuste de texto</InputLabel>
                <Select
                  labelId="text-tools-ai-config-label"
                  label="Configuração para ajuste de texto"
                  value={textToolsConfigId}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTextToolsConfigId(v);
                    try {
                      if (!v) localStorage.removeItem(TEXT_TOOLS_CONFIG_STORAGE_KEY);
                      else localStorage.setItem(TEXT_TOOLS_CONFIG_STORAGE_KEY, String(v));
                    } catch {}
                  }}
                  displayEmpty
                >
                  <MenuItem value="">
                    <em>Usar a configuração ativa padrão</em>
                  </MenuItem>
                  {activeCompleteConfigurations.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name} ({c.provider}
                      {c.model ? ` — ${c.model}` : ''})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>

          {configurations.length === 0 ? (
            <Card variant="outlined" sx={{ borderColor: 'divider', borderRadius: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                  Nenhuma configuração criada. Clique em "Nova Configuração" para começar.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderColor: 'divider', borderRadius: 2 }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Provedor</TableCell>
                    <TableCell>Modelo</TableCell>
                    <TableCell>Documentos</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configurations.map((config) => (
                    <TableRow key={config.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={config.active ? 600 : 400}>
                          {config.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={config.provider} 
                          size="small" 
                          color={config.provider === 'openai' ? 'primary' : config.provider === 'claude' ? 'secondary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {config.model || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        {config.documents_use_all_tags !== false ? (
                          <Chip label="Todas as tags" size="small" variant="outlined" />
                        ) : (config.document_tags || []).length > 0 ? (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(config.document_tags || []).map((t) => (
                              <Chip key={t} label={t} size="small" />
                            ))}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            Nenhuma tag
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {config.active ? (
                          <Chip icon={<CheckCircleIcon />} label="No Orby" color="success" size="small" />
                        ) : (
                          <Chip icon={<CancelIcon />} label="Fora do Orby" size="small" />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          {config.active ? (
                            <Tooltip title="Remover do Orby">
                              <IconButton
                                size="small"
                                onClick={() => handleDeactivateConfig(config.id)}
                              >
                                <ToggleOffIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Disponibilizar no Orby">
                              <IconButton
                                size="small"
                                onClick={() => handleActivateConfig(config.id)}
                                disabled={!config.is_complete}
                              >
                                <CheckCircleIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="Testar">
                            <IconButton
                              size="small"
                              onClick={() => handleTestConfig(config.id)}
                              disabled={testingConfig === config.id || !config.has_api_key}
                            >
                              {testingConfig === config.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PlayArrowIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenConfigModal(config)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteConfig(config.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

      <Dialog
        open={configModalOpen}
        onClose={() => {
          setConfigModalOpen(false);
          setConfigDocTagInput('');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingConfig ? 'Editar Configuração' : 'Nova Configuração'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Nome"
              fullWidth
              value={configForm.name}
              onChange={(e) => setConfigForm({ ...configForm, name: e.target.value })}
              required
            />
            
            <FormControl fullWidth>
              <InputLabel>Provedor</InputLabel>
              <Select
                value={configForm.provider}
                onChange={(e) => {
                  setConfigForm({ ...configForm, provider: e.target.value, model: '' });
                  setAvailableModels([]);
                }}
                label="Provedor"
              >
                <MenuItem value="openai">OpenAI (ChatGPT)</MenuItem>
                <MenuItem value="claude">Anthropic (Claude)</MenuItem>
                <MenuItem value="gemini">Google (Gemini)</MenuItem>
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="API Key"
                type="password"
                fullWidth
                value={configForm.api_key}
                onChange={(e) => {
                  setConfigForm({ ...configForm, api_key: e.target.value });
                  setAvailableModels([]);
                }}
                helperText={editingConfig ? "Deixe em branco para manter a atual" : "Obrigatório"}
                required={!editingConfig}
              />
              <Button
                variant="outlined"
                onClick={handleTestApiKey}
                disabled={loadingModels || !configForm.api_key || !configForm.provider}
                sx={{ minWidth: 120, mt: 0.5 }}
              >
                {loadingModels ? <CircularProgress size={20} /> : 'Buscar Modelos'}
              </Button>
            </Box>

            {availableModels.length > 0 ? (
              <FormControl fullWidth>
                <InputLabel>Modelo</InputLabel>
                <Select
                  value={configForm.model}
                  onChange={(e) => setConfigForm({ ...configForm, model: e.target.value })}
                  label="Modelo"
                >
                  {availableModels.map((model) => (
                    <MenuItem key={model} value={model}>
                      {model}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {availableModels.length} modelo(s) disponível(is)
                </Typography>
              </FormControl>
            ) : (
              <TextField
                label="Modelo"
                fullWidth
                value={configForm.model}
                onChange={(e) => setConfigForm({ ...configForm, model: e.target.value })}
                placeholder={
                  configForm.provider === 'gemini' 
                    ? 'Ex: gemini-2.5-flash, gemini-1.5-pro'
                    : configForm.provider === 'openai'
                    ? 'Ex: gpt-4, gpt-3.5-turbo'
                    : 'Ex: claude-3-haiku-20240307'
                }
                helperText="Clique em 'Buscar Modelos' após preencher a API key ou digite manualmente"
              />
            )}

            <TextField
              label="Instruções do Sistema"
              fullWidth
              multiline
              rows={4}
              value={configForm.system_instructions}
              onChange={(e) => setConfigForm({ ...configForm, system_instructions: e.target.value })}
              placeholder="Instruções personalizadas para a IA (opcional)"
              helperText="Usadas no chat do Orby (assistente). São adicionadas ao contexto do assistente."
            />

            <TextField
              label="Instruções para ajuste de texto"
              fullWidth
              multiline
              rows={4}
              value={configForm.text_tools_instructions}
              onChange={(e) => setConfigForm({ ...configForm, text_tools_instructions: e.target.value })}
              placeholder="Ex.: use tom formal, evite gírias, mantenha termos técnicos... (opcional)"
              helperText="Usadas SÓ nos botões de correção/ajuste de texto dos tickets (modo cru: sem o prompt do chat nem a base de conhecimento)."
            />

            <FormControlLabel
              control={
                <Switch
                  checked={configForm.documents_use_all_tags}
                  onChange={(e) =>
                    setConfigForm({ ...configForm, documents_use_all_tags: e.target.checked })
                  }
                />
              }
              label="Incluir todos os documentos ativos no contexto da IA"
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: -1, mb: 1 }}>
              Se desligar, só entram documentos que tenham pelo menos uma das tags escolhidas abaixo.
            </Typography>

            {!configForm.documents_use_all_tags && (
              <Autocomplete
                multiple
                freeSolo
                options={tagOptionsForConfig.length ? tagOptionsForConfig : tagSuggestions}
                value={configForm.document_tags}
                inputValue={configDocTagInput}
                onInputChange={(e, newInput, reason) => {
                  if (reason === 'input' || reason === 'clear') {
                    setConfigDocTagInput(newInput);
                  }
                }}
                onChange={(e, newValue) => {
                  const cleaned = newValue
                    .map((t) => String(t).trim().toLowerCase())
                    .filter(Boolean);
                  setConfigForm({ ...configForm, document_tags: [...new Set(cleaned)] });
                  setConfigDocTagInput('');
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      {...getTagProps({ index })}
                      key={`${option}-${index}`}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags de documentos para esta IA"
                    placeholder="Criar ou escolher tags"
                    helperText="Enter para confirmar cada tag; o texto pendente também entra ao Salvar a configuração."
                  />
                )}
              />
            )}

            <FormControlLabel
              control={
                <Switch
                  checked={configForm.active}
                  onChange={(e) => setConfigForm({ ...configForm, active: e.target.checked })}
                />
              }
              label="Disponível no assistente Orby (pode haver várias)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfigModalOpen(false);
              setConfigDocTagInput('');
            }}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveConfig}
            variant="contained"
            disabled={loading || !configForm.name || (!editingConfig && !configForm.api_key)}
          >
            {loading ? <CircularProgress size={20} /> : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>
      </CardContent>
    </Card>
  );
};

export default AIConfigurationPage;
